import { Check, Monitor, Moon, Sun } from 'lucide-react';

import { cn } from '../cn';
import { type AccentPreset, type ThemeMode, useTheme } from '../theme';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface ThemeToggleLabels {
  light: string;
  dark: string;
  system: string;
  trigger: string;
  accentGold?: string;
  accentViolet?: string;
  accentRose?: string;
}

const DEFAULT_LABELS: ThemeToggleLabels = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
  trigger: 'Toggle theme',
  accentGold: 'Gold',
  accentViolet: 'Violet',
  accentRose: 'Rose',
};

const OPTIONS: Array<{ mode: ThemeMode; icon: typeof Sun }> = [
  { mode: 'light', icon: Sun },
  { mode: 'dark', icon: Moon },
  { mode: 'system', icon: Monitor },
];

const ACCENT_OPTIONS: Array<{ accent: AccentPreset; swatchClass: string; labelKey: keyof ThemeToggleLabels }> = [
  // Fixed OKLCH (not the themeable `bg-gold`) so the Gold swatch keeps showing
  // gold even when another accent is active and has remapped `--color-gold`.
  { accent: 'gold', swatchClass: 'bg-[oklch(0.74_0.16_90)]', labelKey: 'accentGold' },
  { accent: 'violet', swatchClass: 'bg-[oklch(0.62_0.19_300)]', labelKey: 'accentViolet' },
  { accent: 'rose', swatchClass: 'bg-[oklch(0.64_0.19_350)]', labelKey: 'accentRose' },
];

export interface ThemeToggleProps {
  labels?: ThemeToggleLabels;
  className?: string;
}

/** Cycle through light/dark/system and pick an accent preset via a small dropdown; icon reflects the resolved theme. */
export function ThemeToggle({ labels = DEFAULT_LABELS, className }: ThemeToggleProps) {
  const { mode, resolvedTheme, accent, setMode, setAccent } = useTheme();
  const ActiveIcon = resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={labels.trigger}
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-bg text-ink',
            'transition-colors duration-150 hover:bg-surface motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            className,
          )}
        >
          <ActiveIcon
            key={resolvedTheme}
            className="h-4 w-4 animate-in fade-in-0 zoom-in-50 duration-200 motion-reduce:animate-none"
            aria-hidden
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {OPTIONS.map(({ mode: optionMode, icon: Icon }) => (
          <DropdownMenuItem
            key={optionMode}
            onSelect={() => setMode(optionMode)}
            className="justify-between gap-6"
          >
            <span className="flex items-center gap-2">
              <Icon className="h-4 w-4" aria-hidden />
              {labels[optionMode]}
            </span>
            {mode === optionMode && <Check className="h-4 w-4 text-gold" aria-hidden />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {ACCENT_OPTIONS.map(({ accent: optionAccent, swatchClass, labelKey }) => (
          <DropdownMenuItem
            key={optionAccent}
            onSelect={() => setAccent(optionAccent)}
            className="justify-between gap-6"
          >
            <span className="flex items-center gap-2">
              <span className={cn('h-3.5 w-3.5 rounded-full border border-border', swatchClass)} aria-hidden />
              {labels[labelKey]}
            </span>
            {accent === optionAccent && <Check className="h-4 w-4 text-gold" aria-hidden />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
